require("@netprofit/blockmapsfonts");

const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

const getSamePageAnchorTarget = (link) => {
	if (!(link instanceof HTMLAnchorElement)) {
		return null;
	}

	if (!link.hash || link.hash === "#") {
		return null;
	}

	const currentUrl = new URL(window.location.href);
	const linkUrl = new URL(link.href, window.location.href);

	if (
		currentUrl.origin !== linkUrl.origin ||
		currentUrl.pathname !== linkUrl.pathname ||
		currentUrl.search !== linkUrl.search
	) {
		return null;
	}

	const targetId = decodeURIComponent(link.hash.slice(1));

	if (!targetId) {
		return null;
	}

	return document.getElementById(targetId) || document.querySelector(`[name="${CSS.escape(targetId)}"]`);
};

document.addEventListener("click", (event) => {
	const link = event.target.closest("a");

	if (!link) {
		return;
	}

	const target = getSamePageAnchorTarget(link);

	if (!target) {
		return;
	}

	event.preventDefault();

	target.scrollIntoView({
		behavior: prefersReducedMotion.matches ? "auto" : "smooth",
		block: "start",
	});

	window.history.pushState(null, "", link.hash);

	if (!target.hasAttribute("tabindex")) {
		target.setAttribute("tabindex", "-1");
	}

	target.focus({ preventScroll: true });
});

/* Header mobile menu (rsce_header override)
   Disclosure pattern: the toggle's aria-expanded is the only state, the CSS
   shows #header-menu from it. Below $tablet_l (1024px) only; from there the
   toggle is hidden and the menu is always shown, so the state is reset. */

const HEADER_MENU_TOGGLE = "[data-header-menu-toggle]";
const headerMenuDesktop = window.matchMedia("(min-width: 1024px)");

const getHeaderMenu = (toggle) => document.getElementById(toggle.getAttribute("aria-controls") || "");

const setHeaderMenuOpen = (toggle, open) => {
	toggle.setAttribute("aria-expanded", open ? "true" : "false");
};

const getOpenHeaderMenuToggles = () =>
	Array.from(document.querySelectorAll(`${HEADER_MENU_TOGGLE}[aria-expanded="true"]`));

document.addEventListener("click", (event) => {
	const toggle = event.target.closest(HEADER_MENU_TOGGLE);

	if (toggle) {
		setHeaderMenuOpen(toggle, toggle.getAttribute("aria-expanded") !== "true");
		return;
	}

	getOpenHeaderMenuToggles().forEach((openToggle) => {
		const menu = getHeaderMenu(openToggle);

		// Outside click closes; a link inside the menu closes too (same-page
		// anchors like #contact-form would otherwise leave it open).
		if (!menu || !menu.contains(event.target) || event.target.closest("a")) {
			setHeaderMenuOpen(openToggle, false);
		}
	});
});

document.addEventListener("keydown", (event) => {
	if (event.key !== "Escape") {
		return;
	}

	getOpenHeaderMenuToggles().forEach((toggle) => {
		const menu = getHeaderMenu(toggle);
		const focusWasInside = menu && menu.contains(document.activeElement);

		setHeaderMenuOpen(toggle, false);

		if (focusWasInside || document.activeElement === toggle) {
			toggle.focus();
		}
	});
});

headerMenuDesktop.addEventListener("change", (event) => {
	if (event.matches) {
		getOpenHeaderMenuToggles().forEach((toggle) => setHeaderMenuOpen(toggle, false));
	}
});

/* Teaser preview loop (rsce_teaser override)
   The MP4 thumbnail plays like an animated image, but only without
   prefers-reduced-motion. It has no autoplay attribute (browsers ignore the
   preference for that), so with reduced motion it stays on its first frame. */

const PREVIEW_LOOP_VIDEO = "[data-preview-loop]";

const syncPreviewLoops = () => {
	document.querySelectorAll(PREVIEW_LOOP_VIDEO).forEach((video) => {
		if (prefersReducedMotion.matches) {
			video.pause();
			return;
		}

		video.muted = true;
		video.play().catch(() => {
			/* Autoplay blocked (e.g. iOS Low Power Mode): the first frame stays. */
		});
	});
};

document.addEventListener("DOMContentLoaded", syncPreviewLoops);
prefersReducedMotion.addEventListener("change", syncPreviewLoops);

const getVimeoEmbedUrl = (url) => {
	try {
		const parsedUrl = new URL(url, window.location.href);
		const videoMatch = parsedUrl.pathname.match(/(?:\/video)?\/(\d+)/);
		const videoId = videoMatch ? videoMatch[1] : null;

		if (!videoId) {
			return null;
		}

		const embedUrl = new URL(`https://player.vimeo.com/video/${videoId}`);
		const hash = parsedUrl.searchParams.get("h");

		if (hash) {
			embedUrl.searchParams.set("h", hash);
		}

		embedUrl.searchParams.set("dnt", "1");
		embedUrl.searchParams.set("autoplay", "1");
		embedUrl.searchParams.set("title", "0");
		embedUrl.searchParams.set("byline", "0");
		embedUrl.searchParams.set("portrait", "0");

		return embedUrl.toString();
	} catch (error) {
		return null;
	}
};

document.addEventListener("click", (event) => {
	const embed = event.target.closest("[data-vimeo-embed]");

	if (!embed || event.target.closest("a")) {
		return;
	}

	const embedUrl = getVimeoEmbedUrl(embed.dataset.vimeoUrl || "");

	if (!embedUrl) {
		return;
	}

	const iframe = document.createElement("iframe");
	iframe.className = "vimeo-embed__iframe";
	iframe.src = embedUrl;
	iframe.allow = "autoplay; fullscreen; picture-in-picture";
	iframe.allowFullscreen = true;
	iframe.title = "Vimeo video";

	embed.innerHTML = "";
	embed.appendChild(iframe);
});

/* Course player (MC-TASK-MEMBER-AREA 3.1)
   Sidebar lesson switching plus the 2-click Bunny Stream embed. Exactly one
   iframe can exist at a time: switching lessons restores every frame to its
   server-rendered preview first. */

const COURSE_PLAYER_STORAGE_PREFIX = "mc-course-player:";
const coursePlayerPreviews = new WeakMap();

const restoreCourseFrame = (frame) => {
	const preview = coursePlayerPreviews.get(frame);

	if (typeof preview === "string") {
		frame.innerHTML = preview;
	}
};

const loadCourseFrame = (frame) => {
	const source = frame.dataset.bunnySrc;

	if (!source) {
		return;
	}

	if (!coursePlayerPreviews.has(frame)) {
		coursePlayerPreviews.set(frame, frame.innerHTML);
	}

	const iframe = document.createElement("iframe");
	iframe.className = "course-player__iframe";
	iframe.src = `${source}${source.includes("?") ? "&" : "?"}autoplay=true`;
	iframe.allow = "autoplay; encrypted-media; picture-in-picture; fullscreen";
	iframe.referrerPolicy = "strict-origin-when-cross-origin";
	iframe.allowFullscreen = true;
	iframe.loading = "lazy";
	iframe.title = "Course video";

	frame.innerHTML = "";
	frame.appendChild(iframe);
};

const getCourseTabs = (player) => Array.from(player.querySelectorAll("[data-course-tab]"));

const selectCourseLesson = (player, index, { focusTab = false, persist = true } = {}) => {
	const tabs = getCourseTabs(player);
	const panels = Array.from(player.querySelectorAll("[data-course-panel]"));
	const wanted = String(index);

	if (!tabs.some((tab) => tab.dataset.courseTab === wanted)) {
		return;
	}

	// Tear down every running player before anything becomes visible.
	player.querySelectorAll("[data-bunny-embed]").forEach(restoreCourseFrame);

	tabs.forEach((tab) => {
		const active = tab.dataset.courseTab === wanted;
		tab.setAttribute("aria-selected", active ? "true" : "false");
		tab.tabIndex = active ? 0 : -1;

		if (active && focusTab) {
			tab.focus();
		}
	});

	panels.forEach((panel) => {
		panel.hidden = panel.dataset.coursePanel !== wanted;
	});

	if (persist && player.dataset.coursePlayerKey) {
		try {
			window.localStorage.setItem(COURSE_PLAYER_STORAGE_PREFIX + player.dataset.coursePlayerKey, wanted);
		} catch (error) {
			/* Private mode or a full quota must never break the player. */
		}
	}
};

document.addEventListener("click", (event) => {
	const tab = event.target.closest("[data-course-tab]");

	if (tab) {
		const player = tab.closest("[data-course-player]");

		if (player) {
			selectCourseLesson(player, tab.dataset.courseTab);
		}

		return;
	}

	const frame = event.target.closest("[data-bunny-embed]");

	if (!frame || event.target.closest("a")) {
		return;
	}

	loadCourseFrame(frame);
});

document.addEventListener("keydown", (event) => {
	const tab = event.target.closest("[data-course-tab]");

	if (!tab) {
		return;
	}

	const player = tab.closest("[data-course-player]");

	if (!player) {
		return;
	}

	const tabs = getCourseTabs(player);
	const current = tabs.indexOf(tab);
	let next = null;

	if (event.key === "ArrowDown" || event.key === "ArrowRight") {
		next = (current + 1) % tabs.length;
	} else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
		next = (current - 1 + tabs.length) % tabs.length;
	} else if (event.key === "Home") {
		next = 0;
	} else if (event.key === "End") {
		next = tabs.length - 1;
	}

	if (next === null) {
		return;
	}

	event.preventDefault();
	selectCourseLesson(player, tabs[next].dataset.courseTab, { focusTab: true });
});

document.addEventListener("DOMContentLoaded", () => {
	document.querySelectorAll("[data-course-player]").forEach((player) => {
		const key = player.dataset.coursePlayerKey;

		if (!key) {
			return;
		}

		let stored = null;

		try {
			stored = window.localStorage.getItem(COURSE_PLAYER_STORAGE_PREFIX + key);
		} catch (error) {
			return;
		}

		if (stored !== null) {
			selectCourseLesson(player, stored, { persist: false });
		}
	});
});
